import { useState, useEffect } from 'react';
import { Copy, Download, ScanText, Loader2 } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { copyToClipboard, download, readFileAsDataURL } from '@/lib/utils';
import { toast } from 'sonner';

const LANGS = [
  { value: 'eng', label: 'English' },
  { value: 'hin', label: 'Hindi' },
  { value: 'spa', label: 'Spanish' },
  { value: 'fra', label: 'French' },
  { value: 'deu', label: 'German' },
  { value: 'chi_sim', label: 'Chinese (simplified)' },
  { value: 'jpn', label: 'Japanese' },
  { value: 'ara', label: 'Arabic' },
];

export function Ocr() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [lang, setLang] = useState('eng');
  const [text, setText] = useState('');
  const [progress, setProgress] = useState(0);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    setText('');
    setProgress(0);
    setPreview(await readFileAsDataURL(f));
  }

  async function run() {
    if (!file) return;
    setWorking(true);
    setProgress(0);
    setText('');
    try {
      // Lazy-load tesseract.js (~10MB)
      const Tesseract = (await import('tesseract.js')).default;
      const result = await Tesseract.recognize(file, lang, {
        logger: (m: { status: string; progress: number }) => {
          if (typeof m.progress === 'number') {
            setProgress(Math.round(m.progress * 100));
          }
        },
      });
      setText(result.data.text);
      toast.success('Text extracted');
    } catch (e) {
      toast.error('OCR failed');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="ocr"
        title="OCR — image to text"
        description="Extract text from photos, scans, and screenshots. Powered by Tesseract.js (loads on first use)."
        badges={text ? <Badge variant="success">{text.split(/\s+/).filter(Boolean).length} words</Badge> : null}
      />

      {!file ? (
        <Dropzone
          accept="image/*"
          onFiles={handleFile}
          icon={<ScanText size={20} />}
          hint="JPEG, PNG, GIF, BMP — first run downloads ~10MB of language data"
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="!p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
              <span className="text-sm font-medium truncate">{file.name}</span>
              <Button size="sm" variant="ghost" onClick={() => { setFile(null); setText(''); setPreview(''); }}>
                Change
              </Button>
            </div>
            <div className="p-4 bg-bg-inset min-h-[300px] flex items-center justify-center">
              {preview && (
                <img
                  src={preview}
                  alt="source"
                  className="max-w-full max-h-[400px] rounded shadow-card"
                />
              )}
            </div>
            <div className="px-4 py-3 border-t border-border space-y-3">
              <Field label="Language">
                <Select
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  options={LANGS}
                />
              </Field>
              <Button
                variant="primary"
                className="w-full"
                disabled={working}
                onClick={run}
              >
                {working ? (
                  <>
                    <Loader2 size={11} className="animate-spin" />
                    Extracting · {progress}%
                  </>
                ) : (
                  <>
                    <ScanText size={11} />
                    Extract text
                  </>
                )}
              </Button>
              {working && (
                <div className="h-1 rounded-full bg-bg-inset overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          </Card>

          <Card className="!p-0">
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
              <span className="text-xs font-medium text-fg-muted">Extracted text</span>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!text}
                  onClick={async () => {
                    const ok = await copyToClipboard(text);
                    if (ok) toast.success('Copied');
                  }}
                >
                  <Copy size={11} />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!text}
                  onClick={() => download(text, 'extracted.txt', 'text/plain')}
                >
                  <Download size={11} />
                </Button>
              </div>
            </div>
            <pre className="font-mono text-[13px] leading-relaxed p-4 overflow-auto min-h-[460px] max-h-[560px] whitespace-pre-wrap">
              <code>{text || (working ? 'Working…' : 'Click "Extract text" to OCR this image.')}</code>
            </pre>
          </Card>
        </div>
      )}
    </div>
  );
}

export default Ocr;
